import React from 'react';
import clsx from 'clsx';
import useCheckoutState from '../../hooks/useCheckoutState';
import useSelectedPrice from '../../hooks/useSelectedPrice';
import getThemeLabels from '@repo/checkout/lib/getThemeLabels';
import getPriceBillingConfig from '../../lib/getPriceBillingConfig';
import checkHasValidStartDate from '../../lib/checkHasValidStartDate';
import formatStartDate from '../../lib/formatStartDate';
import { isSinglePwyw as checkIsSinglePwyw, isRecurringPurchase } from '../../lib/multiPrice';
import StyledElement from '../StyledElement';
import Proration from './Proration';

/**
 * Subscription billing context for a single pay-what-you-want SUBSCRIPTION,
 * rendered below the PWYW amount input in the product block.
 *
 * A single PWYW product collects its amount via the input rather than a fixed
 * price line, so `Product`'s `<Price>` component (which owns the trial / start
 * date / payment-plan end / billing-cycle proration lines) is never mounted for
 * it — the input's "/month" suffix carries the interval, but nothing else. For a
 * PWYW SUBSCRIPTION that leaves the buyer without the same terms a fixed-price
 * subscription shows. This fills that gap, reusing the single-price
 * `product.pricing.*` element paths (and the shared `<Proration>`) so the lines
 * match a fixed subscription exactly.
 *
 * Renders nothing unless the lone price is a recurring PWYW price: a one-time
 * PWYW has no subscription terms, and a multi-price product's terms are rendered
 * per-option by the PricePicker.
 */
export default function ProductPwywSubscriptionTerms({ className }) {
  const { checkout, query, theme } = useCheckoutState();
  const product = checkout?.product;
  const onlyPrice = useSelectedPrice();
  const labels = getThemeLabels(theme);

  if (!checkIsSinglePwyw(product, query)) return null;
  if (!onlyPrice || !isRecurringPurchase(onlyPrice, product)) return null;

  const config = getPriceBillingConfig(onlyPrice);
  const trialPeriodDays = config?.trialPeriodDays;
  const startDate = config?.startDate;
  const planIterations = config?.planIterations;

  const hasTrial = trialPeriodDays > 0;
  const hasStartDate = checkHasValidStartDate(startDate);
  const hasEndsAfter = planIterations > 0;

  // Nothing configured beyond the interval (already shown in the input) → render
  // nothing, so the amount input isn't followed by an empty wrapper.
  if (!hasTrial && !hasStartDate && !hasEndsAfter) {
    // Proration self-gates on a billing-cycle anchor, so mount it alone.
    return (
      <div className={clsx('cp_product-pwyw-terms', className)}>
        <Proration price={onlyPrice} />
      </div>
    );
  }

  return (
    <div className={clsx('cp_product-pwyw-terms', className)}>
      {hasTrial && (
        <StyledElement
          elementPath="product.pricing.trialPeriod"
          className={clsx(
            'cp_product-price-trial-period',
            'bg-[var(--cp_product-price-trialPeriod-background-color)]',
            'text-[var(--cp_product-price-trialPeriod-color)]',
            '[font-family:var(--cp_product-price-trialPeriod-font-family)]',
            '[font-style:var(--cp_product-price-trialPeriod-font-style)]',
            '[font-size:var(--cp_product-price-trialPeriod-font-size)]',
            '[line-height:var(--cp_product-price-trialPeriod-line-height)]',
            '[font-weight:var(--cp_product-price-trialPeriod-font-weight)]',
          )}
        >
          <span className="cp_product-price-trial-period-days-text">{trialPeriodDays} </span>
          <span className="cp_product-price-trial-period-days-free-text">
            {labels.trialDaysFreeText}{' '}
          </span>
        </StyledElement>
      )}

      {hasStartDate && (
        <StyledElement
          elementPath="product.pricing.startDate"
          className={clsx(
            'cp_product-price-starts-on-wrapper',
            'bg-[var(--cp_product-price-startDate-background-color)]',
            'text-[var(--cp_product-price-startDate-color)]',
            '[font-family:var(--cp_product-price-startDate-font-family)]',
            '[font-style:var(--cp_product-price-startDate-font-style)]',
            '[font-size:var(--cp_product-price-startDate-font-size)]',
            '[line-height:var(--cp_product-price-startDate-line-height)]',
            '[font-weight:var(--cp_product-price-startDate-font-weight)]',
          )}
        >
          <span className="cp_product-price-starts-on-text">{labels.startsOnText} </span>
          <span className="cp_product-price-starts-on-date">{formatStartDate(startDate)}</span>
        </StyledElement>
      )}

      {hasEndsAfter && (
        <StyledElement
          elementPath="product.pricing.planIterations"
          className={clsx(
            'cp_product-price-ends-after-wrapper',
            'bg-[var(--cp_product-price-planIterations-background-color)]',
            'text-[var(--cp_product-price-planIterations-color)]',
            '[font-family:var(--cp_product-price-planIterations-font-family)]',
            '[font-style:var(--cp_product-price-planIterations-font-style)]',
            '[font-size:var(--cp_product-price-planIterations-font-size)]',
            '[line-height:var(--cp_product-price-planIterations-line-height)]',
            '[font-weight:var(--cp_product-price-planIterations-font-weight)]',
          )}
        >
          <span className="cp_product-price-ends-after-text">{labels.endsAfterText} </span>
          <span className="cp_product-price-ends-after-iterations">{planIterations} </span>
          <span className="cp_product-price-ends-after-payments-text">
            {labels.endsAfterPaymentsText}
          </span>
        </StyledElement>
      )}

      <Proration price={onlyPrice} />
    </div>
  );
}
